import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useTranslation } from "react-i18next";
import { deleteCompanyReport } from "../api/report.api";

// Hook to delete a company report
const useDeleteReport = ({ companyId }: { companyId: number }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [bearerToken, toast, organizationId] = useUIStore(
    useShallow((state: UIState) => [
      state.bearerToken,
      state.toastRef,
      state.organizationId,
    ])
  );

  const deleteReport = (reportId: number) => {
    deleteReportMutation.mutate({ reportId });
  };

  const deleteReportMutation = useMutation({
    mutationFn: async (payload: { reportId: number }) => {
      await deleteCompanyReport(
        companyId,
        organizationId || undefined,
        payload.reportId,
        bearerToken as string
      );
    },
    onError: (error) => {
      console.error("Failed to delete report:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("label-report-delete-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("label-report-deleted"),
        life: 3000,
      });
      // Invalidate reports queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ["reports", companyId] });
      queryClient.invalidateQueries({
        queryKey: ["companyReports", companyId],
      });
    },
  });

  return {
    deleteReport,
    isDeleting: deleteReportMutation.isPending,
  };
};

export default useDeleteReport;
